import { apiClient, postForm } from '@/shared/api/client';
import { submitForm, submitFormData } from '@/shared/api/submit';
import { deleteFile, toUploadedFile, uploadImage, type RawFile, type UploadedFile } from '@/shared/api/upload';
import { getListRows } from '@/shared/lib/pagination';
import type { SelectOption } from '@/shared/ui/selectTypes';
import type { ListParams, ListResponse } from '@/shared/ui/list/types';

const PRIZES_PAGE_SIZE = 20;

export interface RaffleStatus {
  id: number;
  name: string;
  color?: string;
}

/** Raffle endpoints read `page` from the query string (legacy `?page=`). */
export const fetchRaffleList = ({ page }: ListParams) =>
  postForm<ListResponse>('/raffle/list/', {}, { params: { page } });

export async function fetchRaffleStatuses() {
  const res = await postForm<ListResponse>('/raffle/status-list/', {});
  return getListRows<RaffleStatusInfo & Record<string, unknown>>(res, ['data']);
}

export const deleteRaffle = (id: number) => submitForm('/raffle/delete/', { id });

/** Wheel-of-fortune prizes: GET with `auth_token` in the query. */
export const fetchPrizeList = ({ page, is_active }: ListParams) =>
  apiClient
    .get<ListResponse>('/wheel_fortune/prize/list/', {
      params: { page, page_size: PRIZES_PAGE_SIZE, is_active: is_active || undefined },
    })
    .then((res) => res.data);

export const deletePrize = (id: number) => submitForm('/wheel_fortune/prize/delete/', { id });

// ---- prize create / edit ---------------------------------------------------

const PRIZE_BASE = '/wheel_fortune/prize';
const ALL_PRIZES_PAGE_SIZE = 1000;

export const PRIZE_PERIOD_OPTIONS: SelectOption[] = [
  { value: '604800', label: 'Неделя' },
  { value: '2592000', label: 'Месяц' },
];

export interface PrizeValues {
  name: string;
  monthLimit: string;
  image: UploadedFile[];
  minPaymentsSum: string;
  minPaymentsTime: string;
  minCloseBidCount: string;
  minCloseBidTime: string;
}

export const EMPTY_PRIZE: PrizeValues = {
  name: '',
  monthLimit: '',
  image: [],
  minPaymentsSum: '',
  minPaymentsTime: '',
  minCloseBidCount: '',
  minCloseBidTime: '',
};

export interface PrizeItem {
  id: number;
  name: string;
  image?: RawFile | null;
  month_limit?: number | string | null;
  min_payments_sum?: number | string | null;
  min_payments_time?: number | string | null;
  min_close_bid_count?: number | string | null;
  min_close_bid_time?: number | string | null;
}

const str = (value: unknown) => (value === null || value === undefined ? '' : String(value));

export const prizeToValues = (prize: PrizeItem): PrizeValues => {
  const image = toUploadedFile(prize.image);
  return {
    name: prize.name ?? '',
    monthLimit: str(prize.month_limit),
    image: image ? [image] : [],
    minPaymentsSum: str(prize.min_payments_sum),
    minPaymentsTime: str(prize.min_payments_time),
    minCloseBidCount: str(prize.min_close_bid_count),
    minCloseBidTime: str(prize.min_close_bid_time),
  };
};

export const uploadPrizeImage = (file: File) => uploadImage(`${PRIZE_BASE}/upload-image/`, file);
export const deletePrizeImage = (id: number) => deleteFile(`${PRIZE_BASE}/delete-image/`, id);

/** Legacy always sends the two "minimum" amounts (even empty, to clear them on update); the rest only when set. */
export function savePrize(id: number | undefined, v: PrizeValues) {
  const body = new FormData();
  const append = (key: string, value: string | number | undefined, always = false) => {
    if (value || always) body.append(key, String(value ?? ''));
  };
  append('id', id);
  append('name', v.name);
  append('image_id', v.image[0]?.id);
  append('month_limit', v.monthLimit);
  append('min_payments_sum', v.minPaymentsSum, true);
  append('min_payments_time', v.minPaymentsTime);
  append('min_close_bid_count', v.minCloseBidCount, true);
  append('min_close_bid_time', v.minCloseBidTime);
  return submitFormData(`${PRIZE_BASE}/${id ? 'update' : 'create'}/`, body);
}

// ---- wheel sectors (set-active) --------------------------------------------

/** All prizes (not just the visible page) for the wheel sector selects. */
export async function fetchPrizeOptions(): Promise<SelectOption[]> {
  const { data } = await apiClient.get<ListResponse>(`${PRIZE_BASE}/list/`, {
    params: { page: 1, page_size: ALL_PRIZES_PAGE_SIZE },
  });
  return getListRows<{ id: number; name: string }>(data, ['data']).map(({ id, name }) => ({
    value: String(id),
    label: name,
  }));
}

/** Prize ids of the three wheel sector groups currently in use. */
export async function fetchActivePrizeGroups(): Promise<string[][]> {
  const { data } = await apiClient.get<{ data?: Array<Array<{ id: number }>> }>(`${PRIZE_BASE}/get-active/`);
  return Array.from({ length: SECTOR_COUNT }, (_, index) => (data.data?.[index] ?? []).map(({ id }) => String(id)));
}

export const SECTOR_COUNT = 3;

export const setActivePrizes = (groups: string[][]) =>
  submitForm(
    `${PRIZE_BASE}/set-active/`,
    Object.fromEntries(groups.map((ids, index) => [`prize_ids[${index}]`, ids])),
  );

// ---- raffle view -----------------------------------------------------------

export interface RaffleStatusInfo extends RaffleStatus {
  can_played?: boolean | number;
}

export interface RaffleResult {
  id: number;
  coupon_number?: string | number;
  user_name?: string;
  approved?: boolean | number;
  user: { id?: number; name?: string; phone?: string; city_name?: string };
}

export interface RafflePrize {
  id: number;
  name: string;
  is_main?: boolean | number;
  image?: { thumb_140_140?: string } | null;
  result?: RaffleResult | null;
}

export interface RaffleInfo {
  id: number;
  name: string;
  status_id: number;
  prizes: RafflePrize[];
}

export const RAFFLE_STATUS = { ACTIVE: 2, CLOSED: 4 } as const;

export const fetchRaffleInfo = (id: number) =>
  apiClient.get<{ data: RaffleInfo }>('/raffle/info/', { params: { id } }).then((res) => res.data.data);

interface RaffleInfoEnvelope {
  success?: number;
  data?: RaffleInfo;
}

/** Plays the prize with that id; resolves with the updated raffle. */
export const playRafflePrize = (prizeId: number) =>
  submitForm<RaffleInfoEnvelope>('/raffle/play/', { id: prizeId }).then((res) => res.data);

export const publishRaffle = (id: number) =>
  submitForm<RaffleInfoEnvelope>('/raffle/publish/', { id }).then((res) => res.data);

/** Confirms the drawn winner with the (possibly corrected) name and city. */
export const approveRaffleResult = (resultId: number, v: { userName: string; city: string }) =>
  submitForm('/raffle/moderate-result/', {
    id: resultId,
    approved: true,
    user_name: v.userName,
    city: v.city,
  });
